import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Tag,
  BarChart3,
  DollarSign,
  Heart,
  MessageCircle,
  Share2,
  Eye,
  ExternalLink,
  Edit,
  Instagram,
  Facebook,
  Twitter,
  Youtube,
  Linkedin,
  Zap,
  Calendar,
  Filter,
  Search,
  Info,
} from 'lucide-react';
import TikTokIcon from '../ui/TikTokIcon';
import { Card, Tooltip } from '../ui';
import { useAttributionTagStore } from '@/stores/attributionTagStore';
import useGlobalCustomerStore from '@/stores/globalCustomerStore';
import StatusBadge from './StatusBadge';
import { AttributionTagStatus, TagPost } from '@/types/attributionTag';
import { attributionTagService } from '@/services/attributionTagService';

interface DateRange {
  start: string;
  end: string;
}

// Default to 360 days (within the 1-year API limit)
const getDefault360DayRange = (): DateRange => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 360);
  return { start: start.toISOString().split('T')[0], end: end.toISOString().split('T')[0] };
};

interface HashtagActivation {
  id: string;
  name: string;
  description: string | null;
  partner_id: string;
  partner_name: string | null;
  partner_logo_url: string | null;
  activation_type_id: string | null;
  activation_type_name: string | null;
  deal_value: number | null;
  emv_target_value: number | null;
  start_date: string | null;
  end_date: string | null;
  status: string;
  created_at: string | null;
  // EMV metrics from full asset-matching pattern
  total_posts: number;
  total_emv: number;
  total_views: number;
  total_engagement: number;
}


interface AttributionTagInsightsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  hashtagId: number | null;
  hashtag: string | null;
  period?: 'day' | 'week' | 'month' | '60days' | '90days' | 'custom';
  customDateRange?: DateRange | null;
  onEditClick?: (hashtagId: number) => void;
}

const AttributionTagInsightsDrawer: React.FC<AttributionTagInsightsDrawerProps> = ({
  isOpen,
  onClose,
  hashtagId,
  hashtag,
  period = 'custom',
  customDateRange = getDefault360DayRange(),
  onEditClick,
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'posts' | 'activations'>('overview');
  const [tagPosts, setTagPosts] = useState<TagPost[]>([]);
  const [loading, setLoading] = useState(false);
  const [postsLoading, setPostsLoading] = useState(false);

  // Activations state
  const [activations, setActivations] = useState<HashtagActivation[]>([]);
  const [activationsLoading, setActivationsLoading] = useState(false);
  const [activationStatusFilter, setActivationStatusFilter] = useState<string>('all');

  // Posts search state
  const [postsSearch, setPostsSearch] = useState('');

  // Status priority for sorting (Active first)
  const statusPriority: Record<string, number> = {
    'Active': 0,
    'Pending': 1,
    'Suspended': 2,
    'Completed': 3,
  };

  // Filter and sort activations
  const filteredActivations = activations
    .filter(a => activationStatusFilter === 'all' || a.status === activationStatusFilter)
    .sort((a, b) => {
      // First sort by status priority
      const statusA = statusPriority[a.status] ?? 99;
      const statusB = statusPriority[b.status] ?? 99;
      if (statusA !== statusB) return statusA - statusB;
      // Then sort by EMV descending
      return b.total_emv - a.total_emv;
    });

  // Filter and sort posts - by EMV descending, with search
  const filteredPosts = tagPosts
    .filter(p => {
      if (!postsSearch.trim()) return true;
      const searchLower = postsSearch.toLowerCase();
      return (
        (p.caption && p.caption.toLowerCase().includes(searchLower)) ||
        String(p.post_id).includes(searchLower)
      );
    })
    .sort((a, b) => Number(b.emv) - Number(a.emv));

  const { selectedCustomer } = useGlobalCustomerStore();
  const { selectedTag, loadTagDetail, loadTagPosts, isDetailLoading } = useAttributionTagStore();

  // Reset search when drawer closes or hashtag changes
  useEffect(() => {
    if (!isOpen) {
      setPostsSearch('');
      setActivationStatusFilter('all');
    }
  }, [isOpen]);

  // Load tag details, posts, and activations when hashtagId changes
  useEffect(() => {
    if (hashtagId && selectedCustomer?.id && isOpen) {
      setPostsSearch(''); // Reset search when loading new tag
      loadTagData();
      loadPosts();
      loadActivations();
    }
  }, [hashtagId, selectedCustomer?.id, isOpen, period, customDateRange]);

  const loadTagData = async () => {
    if (!hashtagId || !selectedCustomer?.id) return;

    try {
      setLoading(true);
      await loadTagDetail(hashtagId, {
        period,
        ...(period === 'custom' && customDateRange && {
          customStartDate: customDateRange.start,
          customEndDate: customDateRange.end,
        }),
      });
    } catch (error) {
      console.error('Failed to load tag data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPosts = async () => {
    if (!hashtagId || !selectedCustomer?.id) return;

    try {
      setPostsLoading(true);
      const posts = await loadTagPosts(hashtagId, {
        period,
        limit: 20,
        ...(period === 'custom' && customDateRange && {
          customStartDate: customDateRange.start,
          customEndDate: customDateRange.end,
        }),
      });
      setTagPosts(posts);
    } catch (error) {
      console.error('Failed to load tag posts:', error);
    } finally {
      setPostsLoading(false);
    }
  };

  const loadActivations = async () => {
    if (!hashtagId || !selectedCustomer?.id) return;

    try {
      setActivationsLoading(true);
      const response = await attributionTagService.getHashtagActivations(
        hashtagId,
        selectedCustomer.id
      );
      setActivations(response.data || []);
    } catch (error) {
      console.error('Failed to load hashtag activations:', error);
      setActivations([]);
    } finally {
      setActivationsLoading(false);
    }
  };


  const formatActivationDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getActivationStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-700';
      case 'completed':
        return 'bg-blue-100 text-blue-700';
      case 'draft':
        return 'bg-gray-100 text-gray-700';
      case 'pending':
        return 'bg-yellow-100 text-yellow-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  // Get platform icon
  const getPlatformIcon = (platformId: number) => {
    switch (platformId) {
      case 1:
        return Instagram;
      case 2:
        return TikTokIcon;
      case 3:
        return Facebook;
      case 4:
        return Twitter;
      case 5:
        return Youtube;
      case 6:
        return Linkedin;
      default:
        return Instagram;
    }
  };

  // Get platform color
  const getPlatformColor = (platformId: number) => {
    switch (platformId) {
      case 1:
        return 'text-pink-500';
      case 2:
        return 'text-black';
      case 3:
        return 'text-blue-600';
      case 4:
        return 'text-blue-400';
      case 5:
        return 'text-red-600';
      case 6:
        return 'text-blue-700';
      default:
        return 'text-gray-500';
    }
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const formatCurrency = (num: number | string) => {
    const val = Number(num) || 0;
    return `$${val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const truncateCaption = (caption: string | null, maxLength: number = 150) => {
    if (!caption) return '';
    if (caption.length <= maxLength) return caption;
    return caption.substring(0, maxLength) + '...';
  };

  const handleEditClick = () => {
    if (onEditClick && hashtagId) {
      onEditClick(hashtagId);
    }
  };

  // Extract metrics from selectedTag
  const metrics = selectedTag?.metrics;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={onClose}
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
            className="fixed right-0 top-0 h-full w-full max-w-2xl bg-white shadow-xl z-50 flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg">
                  <Tag className="h-5 w-5 text-white" />
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <h2 className="text-xl font-semibold text-gray-900">{hashtag}</h2>
                    {selectedTag?.status && (
                      <StatusBadge status={selectedTag.status as AttributionTagStatus} size="sm" />
                    )}
                  </div>
                  <p className="text-sm text-gray-500">Attribution Tag Analytics</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {onEditClick && (
                  <button
                    onClick={handleEditClick}
                    className="flex items-center px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                  >
                    <Edit className="h-3.5 w-3.5 mr-1.5" />
                    Edit Tag
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-gray-400" />
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-200">
              <button
                onClick={() => setActiveTab('overview')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'overview'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab('activations')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'activations'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Activations ({activations.length})
              </button>
              <button
                onClick={() => setActiveTab('posts')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'posts'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Posts ({tagPosts.length})
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              {(loading || isDetailLoading) && (
                <div className="flex items-center justify-center h-64">
                  <div className="flex items-center space-x-3">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    <span className="text-xs text-gray-900">Loading tag details...</span>
                  </div>
                </div>
              )}

              {!loading && !isDetailLoading && activeTab === 'overview' && (
                <div className="p-6 space-y-4">
                  {/* Primary Metrics Grid */}
                  <div className="grid grid-cols-2 gap-3">
                    <Card className="px-3 py-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-medium text-gray-500 flex items-center gap-1">
                            Total Posts
                            <Tooltip
                              content="Posts that include this attribution tag and match an asset's criteria."
                              placement="top"
                              maxWidth={200}
                            >
                              <Info className="h-3 w-3 text-gray-400 hover:text-gray-600 cursor-help" />
                            </Tooltip>
                          </p>
                          <p className="text-base font-bold text-gray-900">
                            {metrics ? formatNumber(metrics.post_count) : '0'}
                          </p>
                        </div>
                        <div className="p-1.5 bg-blue-100 rounded-md">
                          <BarChart3 className="h-3.5 w-3.5 text-blue-600" />
                        </div>
                      </div>
                    </Card>

                    <Card className="px-3 py-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-medium text-gray-500 flex items-center gap-1">
                            Total EMV
                            <Tooltip
                              content="Earned Media Value from all posts using this tag. Posts matching multiple tags contribute to each tag's total."
                              placement="top"
                              maxWidth={220}
                            >
                              <Info className="h-3 w-3 text-gray-400 hover:text-gray-600 cursor-help" />
                            </Tooltip>
                          </p>
                          <p className="text-base font-bold text-green-600">
                            {metrics ? formatCurrency(metrics.total_emv) : '$0.00'}
                          </p>
                        </div>
                        <div className="p-1.5 bg-green-100 rounded-md">
                          <DollarSign className="h-3.5 w-3.5 text-green-600" />
                        </div>
                      </div>
                    </Card>

                    <Card className="px-3 py-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-medium text-gray-500 flex items-center gap-1">
                            Total Views
                            <Tooltip
                              content="Combined view count from all posts with this tag."
                              placement="top"
                              maxWidth={180}
                            >
                              <Info className="h-3 w-3 text-gray-400 hover:text-gray-600 cursor-help" />
                            </Tooltip>
                          </p>
                          <p className="text-base font-bold text-gray-900">
                            {metrics ? formatNumber(metrics.total_views) : '0'}
                          </p>
                        </div>
                        <div className="p-1.5 bg-purple-100 rounded-md">
                          <Eye className="h-3.5 w-3.5 text-purple-600" />
                        </div>
                      </div>
                    </Card>

                    <Card className="px-3 py-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-medium text-gray-500 flex items-center gap-1">
                            Total Likes
                            <Tooltip
                              content="Combined like count from all posts with this tag."
                              placement="top"
                              maxWidth={180}
                            >
                              <Info className="h-3 w-3 text-gray-400 hover:text-gray-600 cursor-help" />
                            </Tooltip>
                          </p>
                          <p className="text-base font-bold text-gray-900">
                            {metrics ? formatNumber(metrics.total_likes) : '0'}
                          </p>
                        </div>
                        <div className="p-1.5 bg-red-100 rounded-md">
                          <Heart className="h-3.5 w-3.5 text-red-600" />
                        </div>
                      </div>
                    </Card>
                  </div>

                  {/* Average Post Performance */}
                  <Card className="px-4 py-3 bg-gradient-to-br from-slate-50 to-gray-100 border-slate-200">
                    <h3 className="text-xs font-semibold text-gray-900 mb-3 flex items-center gap-1">
                      Average Post Performance
                      <Tooltip
                        content="Average metrics per post using this attribution tag."
                        placement="top"
                        maxWidth={200}
                      >
                        <Info className="h-3 w-3 text-gray-400 hover:text-gray-600 cursor-help" />
                      </Tooltip>
                    </h3>
                    <div className="grid grid-cols-4 gap-2">
                      <div className="bg-white rounded-lg p-2 shadow-sm border border-green-100">
                        <div className="flex items-center justify-center mb-1">
                          <DollarSign className="h-3.5 w-3.5 text-green-500" />
                        </div>
                        <p className="text-xs font-bold text-green-600 text-center">
                          {metrics ? formatCurrency(metrics.avg_emv_per_post) : '$0.00'}
                        </p>
                        <p className="text-[10px] text-gray-500 text-center mt-0.5">EMV</p>
                      </div>
                      <div className="bg-white rounded-lg p-2 shadow-sm border border-purple-100">
                        <div className="flex items-center justify-center mb-1">
                          <Eye className="h-3.5 w-3.5 text-purple-500" />
                        </div>
                        <p className="text-xs font-bold text-gray-900 text-center">
                          {metrics && metrics.post_count > 0
                            ? formatNumber(Math.round(metrics.total_views / metrics.post_count))
                            : '0'}
                        </p>
                        <p className="text-[10px] text-gray-500 text-center mt-0.5">Views</p>
                      </div>
                      <div className="bg-white rounded-lg p-2 shadow-sm border border-red-100">
                        <div className="flex items-center justify-center mb-1">
                          <Heart className="h-3.5 w-3.5 text-red-500" />
                        </div>
                        <p className="text-xs font-bold text-gray-900 text-center">
                          {metrics && metrics.post_count > 0
                            ? formatNumber(Math.round(metrics.total_likes / metrics.post_count))
                            : '0'}
                        </p>
                        <p className="text-[10px] text-gray-500 text-center mt-0.5">Likes</p>
                      </div>
                      <div className="bg-white rounded-lg p-2 shadow-sm border border-blue-100">
                        <div className="flex items-center justify-center mb-1">
                          <MessageCircle className="h-3.5 w-3.5 text-blue-500" />
                        </div>
                        <p className="text-xs font-bold text-gray-900 text-center">
                          {metrics && metrics.post_count > 0
                            ? formatNumber(Math.round(metrics.total_comments / metrics.post_count))
                            : '0'}
                        </p>
                        <p className="text-[10px] text-gray-500 text-center mt-0.5">Comments</p>
                      </div>
                    </div>
                  </Card>

                  {/* Engagement Breakdown */}
                  <Card className="px-3 py-2">
                    <h3 className="text-xs font-semibold text-gray-900 mb-2 flex items-center gap-1">
                      Engagement Breakdown
                      <Tooltip
                        content="Total engagement metrics from all posts with this tag."
                        placement="top"
                        maxWidth={200}
                      >
                        <Info className="h-3 w-3 text-gray-400 hover:text-gray-600 cursor-help" />
                      </Tooltip>
                    </h3>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <MessageCircle className="h-3.5 w-3.5 text-blue-500" />
                          <span className="text-xs font-medium text-gray-700">Comments</span>
                        </div>
                        <span className="text-xs font-semibold text-gray-900">
                          {metrics ? formatNumber(metrics.total_comments) : '0'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <Share2 className="h-3.5 w-3.5 text-green-500" />
                          <span className="text-xs font-medium text-gray-700">Shares</span>
                        </div>
                        <span className="text-xs font-semibold text-gray-900">
                          {metrics ? formatNumber(metrics.total_shares) : '0'}
                        </span>
                      </div>
                    </div>
                  </Card>
                </div>
              )}

              {!loading && !isDetailLoading && activeTab === 'posts' && (
                <div className="p-6">
                  {/* Search bar */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="relative w-1/3">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                      <input
                        type="text"
                        value={postsSearch}
                        onChange={(e) => setPostsSearch(e.target.value)}
                        placeholder="Search posts..."
                        className="w-full pl-8 pr-8 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      {postsSearch && (
                        <button
                          onClick={() => setPostsSearch('')}
                          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                    {postsSearch && (
                      <p className="text-xs text-gray-500">
                        {filteredPosts.length} of {tagPosts.length} posts
                      </p>
                    )}
                  </div>

                  {postsLoading ? (
                    <div className="flex items-center justify-center h-64">
                      <div className="flex items-center space-x-3">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                        <span className="text-xs text-gray-900">Loading posts...</span>
                      </div>
                    </div>
                  ) : filteredPosts.length > 0 ? (
                    <div className="space-y-4">
                      {filteredPosts.map((post, index) => {
                        const PlatformIcon = getPlatformIcon(post.platform_id);

                        return (
                          <motion.div
                            key={`${post.partition_key}-${post.post_id}`}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05 }}
                          >
                            <Card className="p-4 hover:shadow-md transition-shadow">
                              <div className="flex items-start space-x-4">
                                <div className="flex-shrink-0">
                                  <div
                                    className={`p-2 rounded-lg ${getPlatformColor(post.platform_id)}`}
                                  >
                                    <PlatformIcon className="h-5 w-5" />
                                  </div>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center space-x-2">
                                      <p className="text-xs font-medium text-gray-900">
                                        Post #{post.post_id}
                                      </p>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                      <span className="text-sm font-bold text-green-600">
                                        {formatCurrency(post.emv)}
                                      </span>
                                      {post.post_url && (
                                        <a
                                          href={post.post_url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                          }}
                                        >
                                          <ExternalLink className="h-4 w-4" />
                                        </a>
                                      )}
                                    </div>
                                  </div>
                                  <p className="text-xs text-gray-600 mb-3">
                                    {truncateCaption(post.caption)}
                                  </p>
                                  <div className="flex items-center justify-between text-xs text-gray-500">
                                    <div className="flex items-center space-x-4">
                                      <div className="flex items-center space-x-1">
                                        <Eye className="h-3 w-3" />
                                        <span>{formatNumber(post.views_count)}</span>
                                      </div>
                                      <div className="flex items-center space-x-1">
                                        <Heart className="h-3 w-3" />
                                        <span>{formatNumber(post.likes_count)}</span>
                                      </div>
                                      <div className="flex items-center space-x-1">
                                        <MessageCircle className="h-3 w-3" />
                                        <span>{formatNumber(post.comments_count)}</span>
                                      </div>
                                      <div className="flex items-center space-x-1">
                                        <Share2 className="h-3 w-3" />
                                        <span>{formatNumber(post.shares_count)}</span>
                                      </div>
                                    </div>
                                    <span>{formatDate(post.posted_at)}</span>
                                  </div>
                                </div>
                              </div>
                            </Card>
                          </motion.div>
                        );
                      })}
                    </div>
                  ) : tagPosts.length === 0 ? (
                    <div className="text-center py-12">
                      <Tag className="mx-auto h-10 w-10 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">No posts found</h3>
                      <p className="mt-1 text-xs text-gray-500">
                        No posts found for this tag in the selected period.
                      </p>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Search className="mx-auto h-10 w-10 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">No matching posts</h3>
                      <p className="mt-1 text-xs text-gray-500">
                        No posts match your search criteria.
                      </p>
                      <button
                        onClick={() => setPostsSearch('')}
                        className="mt-3 text-xs text-blue-600 hover:text-blue-700 font-medium"
                      >
                        Clear search
                      </button>
                    </div>
                  )}
                </div>
              )}

              {!loading && !isDetailLoading && activeTab === 'activations' && (
                <div className="p-6">
                  {/* Status Filter */}
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs text-gray-500">
                      {filteredActivations.length} of {activations.length} activations
                    </span>
                    <div className="relative">
                      <Filter className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                      <select
                        value={activationStatusFilter}
                        onChange={(e) => setActivationStatusFilter(e.target.value)}
                        className="pl-8 pr-8 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white cursor-pointer"
                      >
                        <option value="all">All Statuses</option>
                        <option value="Active">Active ({activations.filter(a => a.status === 'Active').length})</option>
                        <option value="Pending">Pending ({activations.filter(a => a.status === 'Pending').length})</option>
                        <option value="Suspended">Suspended ({activations.filter(a => a.status === 'Suspended').length})</option>
                        <option value="Completed">Completed ({activations.filter(a => a.status === 'Completed').length})</option>
                      </select>
                    </div>
                  </div>

                  {activationsLoading ? (
                    <div className="flex items-center justify-center h-64">
                      <div className="flex items-center space-x-3">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                        <span className="text-xs text-gray-900">Loading activations...</span>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filteredActivations.map((activation, index) => (
                        <motion.div
                          key={activation.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.03 }}
                        >
                          <Card className="overflow-hidden p-4">
                            {/* Activation Header */}
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center space-x-3 min-w-0">
                                <div className="p-2 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg flex-shrink-0">
                                  <Zap className="h-4 w-4 text-white" />
                                </div>
                                <div className="min-w-0">
                                  <p className="text-sm font-semibold text-gray-900 truncate">
                                    {activation.name}
                                  </p>
                                  <div className="flex items-center space-x-2 mt-0.5">
                                    {activation.partner_name && (
                                      <span className="text-xs text-gray-500 truncate">
                                        {activation.partner_name}
                                      </span>
                                    )}
                                    <span
                                      className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${getActivationStatusColor(
                                        activation.status
                                      )}`}
                                    >
                                      {activation.status}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              {activation.start_date && (
                                <div className="flex items-center text-xs text-gray-500 flex-shrink-0">
                                  <Calendar className="h-3 w-3 mr-1" />
                                  <span>
                                    {formatActivationDate(activation.start_date)}
                                    {activation.end_date && ` - ${formatActivationDate(activation.end_date)}`}
                                  </span>
                                </div>
                              )}
                            </div>

                            {/* Performance Metrics - Always visible */}
                            <div className="grid grid-cols-4 gap-2">
                              <div className="bg-gray-50 rounded-lg p-2 border border-gray-100">
                                <div className="flex items-center gap-1.5">
                                  <BarChart3 className="h-3 w-3 text-blue-500" />
                                  <span className="text-[10px] text-gray-500">Posts</span>
                                </div>
                                <p className="text-sm font-bold text-gray-900 mt-0.5">
                                  {formatNumber(activation.total_posts)}
                                </p>
                              </div>
                              <div className="bg-gray-50 rounded-lg p-2 border border-gray-100">
                                <div className="flex items-center gap-1.5">
                                  <DollarSign className="h-3 w-3 text-green-500" />
                                  <span className="text-[10px] text-gray-500">EMV</span>
                                </div>
                                <p className="text-sm font-bold text-green-600 mt-0.5">
                                  {formatCurrency(activation.total_emv)}
                                </p>
                              </div>
                              <div className="bg-gray-50 rounded-lg p-2 border border-gray-100">
                                <div className="flex items-center gap-1.5">
                                  <Eye className="h-3 w-3 text-purple-500" />
                                  <span className="text-[10px] text-gray-500">Views</span>
                                </div>
                                <p className="text-sm font-bold text-gray-900 mt-0.5">
                                  {formatNumber(activation.total_views)}
                                </p>
                              </div>
                              <div className="bg-gray-50 rounded-lg p-2 border border-gray-100">
                                <div className="flex items-center gap-1.5">
                                  <Heart className="h-3 w-3 text-red-500" />
                                  <span className="text-[10px] text-gray-500">Engagement</span>
                                </div>
                                <p className="text-sm font-bold text-gray-900 mt-0.5">
                                  {formatNumber(activation.total_engagement)}
                                </p>
                              </div>
                            </div>
                          </Card>
                        </motion.div>
                      ))}
                      {filteredActivations.length === 0 && activations.length === 0 && (
                        <div className="text-center py-12">
                          <Zap className="mx-auto h-10 w-10 text-gray-400" />
                          <h3 className="mt-2 text-sm font-medium text-gray-900">No activations found</h3>
                          <p className="mt-1 text-xs text-gray-500">
                            This tag is not linked to any activations yet.
                          </p>
                        </div>
                      )}
                      {filteredActivations.length === 0 && activations.length > 0 && (
                        <div className="text-center py-12">
                          <Filter className="mx-auto h-10 w-10 text-gray-400" />
                          <h3 className="mt-2 text-sm font-medium text-gray-900">No matching activations</h3>
                          <p className="mt-1 text-xs text-gray-500">
                            No activations match the selected filter.
                          </p>
                          <button
                            onClick={() => setActivationStatusFilter('all')}
                            className="mt-3 text-xs text-blue-600 hover:text-blue-700 font-medium"
                          >
                            Clear filter
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default AttributionTagInsightsDrawer;
